// ============================================================
// TERROR ENGINE — PROXIMITY SYSTEM
// Bubble/verb detection and display. Used by every walkable zone.
//
// How it works:
//   1. Define interactable objects in your ZoneConfig with { x, y, rx, ry, ... }
//   2. Call getNearby(pos, objects) each movement tick
//   3. Render <ProximityBubble> when something is nearby
//
// The bubble style matches the Plaza's existing pill aesthetic.
// ============================================================

import { useEffect, useRef } from "react";
import { SD } from "./theme";

// ── Proximity detection ───────────────────────────────────────
/**
 * Find the nearest interactable object within range.
 * @param {{ x: number, y: number }} pos  — current tile position
 * @param {Array} objects                 — array of { x, y, rx?, ry?, ... }
 * @returns the first matching object or null
 */
export function getNearby(pos, objects) {
  if (!objects || !pos) return null;
  return objects.find(obj => {
    const rx = obj.rx ?? 2;
    const ry = obj.ry ?? 1;
    return Math.abs(pos.x - obj.x) <= rx && Math.abs(pos.y - obj.y) <= ry;
  }) || null;
}

/**
 * Check if another player is within challenge range.
 * @param {{ x: number, y: number }} pos   — local player tile position
 * @param {Object} players                 — { [uid]: { x, y, name } }
 * @param {number} range                   — tile range (default 2)
 * @returns { targetUid, targetName } or null
 */
export function getNearbyPlayer(pos, players, range = 2) {
  if (!players || !pos) return null;
  const found = Object.entries(players).find(([, p]) =>
    Math.abs(p.x - pos.x) <= range && Math.abs(p.y - pos.y) <= range
  );
  if (!found) return null;
  const [targetUid, targetData] = found;
  return { targetUid, targetName: targetData.name };
}

// ── Proximity bubble UI ───────────────────────────────────────
/**
 * Floating verb pill. Appears above interactable objects.
 * Handles keyboard (Q / Enter = activate, Escape = dismiss).
 *
 * Props:
 *   verb       string         — "Speak" | "Match" | "Solrend" | etc.
 *   label      string         — sub-label shown below verb
 *   color      string         — accent color
 *   tileX      number         — tile X of object (for absolute positioning)
 *   tileY      number         — tile Y of object
 *   tile       number         — tile size in px
 *   portrait   boolean        — use portrait (fixed center-bottom) instead of absolute
 *   onActivate function
 *   onDismiss  function
 */
export function ProximityBubble({
  verb        = "Talk",
  label       = "",
  color       = SD.purpleLight,
  tileX       = 0,
  tileY       = 0,
  tile        = 48,
  portrait    = false,    // if true: fixed center-bottom (portrait phone layout)
  onActivate,
  onDismiss,
  comingSoon  = false,
}) {
  const readyRef = useRef(false);

  // Delay keyboard activation slightly to avoid accidental triggers
  useEffect(() => {
    const t = setTimeout(() => { readyRef.current = true; }, 400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (!readyRef.current) return;
      if (e.key === "q" || e.key === "Q" || e.key === "Enter") {
        e.preventDefault(); onActivate?.();
      }
      if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault(); onDismiss?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onActivate, onDismiss]);

  const left   = tileX * tile + tile / 2;
  const top    = tileY * tile - 18;

  return (
    <div
      onClick={onActivate}
      onContextMenu={e => { e.preventDefault(); onActivate?.(); }}
      style={{
        position: portrait ? "fixed" : "absolute",
        left:     portrait ? "50%"   : left,
        top:      portrait ? undefined : top,
        bottom:   portrait ? 140 : undefined,
        transform: "translateX(-50%)",
        zIndex: 160, cursor: "pointer", userSelect: "none",
        animation: "bubbleFloat 1.8s ease-in-out infinite alternate",
        pointerEvents: comingSoon ? "none" : "auto",
        opacity: comingSoon ? 0.5 : 1,
      }}
    >
      <div style={{
        background: `linear-gradient(135deg, ${color}18, rgba(4,2,14,0.95))`,
        border: `1px solid ${color}88`,
        borderRadius: 10, padding: "10px 24px",
        textAlign: "center",
        boxShadow: `0 0 18px ${color}33`,
        fontFamily: SD.fontDisplay,
      }}>
        <div style={{
          fontSize: 13, fontWeight: 900,
          color: color, letterSpacing: 2,
          textShadow: `0 0 8px ${color}`,
        }}>
          ◆ {verb}
        </div>
        {(label || comingSoon) && (
          <div style={{ fontSize: 9, color: "#ffffff55", marginTop: 3, letterSpacing: 1 }}>
            {label}{comingSoon ? " · Soon" : ""} · Q
          </div>
        )}
      </div>
    </div>
  );
}

// ChallengePill and ChallengeModal live in ChallengeSystem.js
// Import from there: import { ChallengePill, ChallengeModal } from "./ChallengeSystem";
